import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import Swal from "sweetalert2";
import Table from "../../core/pagination/datatable";
import { getPatients } from "../../core/api/patientService";
import { getSalesReport } from "../../core/api/reportService";
import { getApiErrorMessage } from "../../core/utils/apiError";

const today = () => new Date().toISOString().split("T")[0];
const DEFAULT_FILTERS = { from: today(), to: today(), patientId: "", status: "" };
const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "Paid", label: "Paid" },
  { value: "PartialPaid", label: "Partial Paid" },
  { value: "Draft", label: "Draft" },
  { value: "Cancelled", label: "Cancelled" },
];

const STATUS_BADGE = { Paid: "success", PartialPaid: "warning", Draft: "secondary", Cancelled: "danger" };

// Note: the /api/reports/sales endpoint filters by patientId, not doctorId (confirmed
// against the live swagger spec) — hence a patient filter rather than a doctor filter here.
const SalesReport = () => {
  const [patients, setPatients] = useState([]);
  const [patientSearch, setPatientSearch] = useState("");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getPatients().then((res) => setPatients(res.data || [])).catch(() => {});
  }, []);

  const patientOptions = useMemo(() => {
    const q = patientSearch.trim().toLowerCase();
    const filtered = q
      ? patients.filter((p) => p.patientName?.toLowerCase().includes(q) || p.mobileNumber?.includes(q))
      : patients;
    return filtered.map((p) => ({ value: p.id, label: `${p.patientName} — ${p.mobileNumber}` }));
  }, [patients, patientSearch]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { runReport(); }, []);

  const runReport = async (f = filters) => {
    setLoading(true);
    try {
      const res = await getSalesReport(f);
      const payload = res.data || {};
      setData(payload.records || payload || []);
      setSummary(payload.summary || null);
    } catch (err) {
      Swal.fire("Error", getApiErrorMessage(err, "Failed to load report."), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
    runReport(DEFAULT_FILTERS);
  };

  const columns = [
    { title: "Invoice #", dataIndex: "invoiceNumber", width: 120 },
    { title: "Date", dataIndex: "invoiceDate", width: 110, render: (v) => v ? new Date(v).toLocaleDateString("en-IN") : "—" },
    { title: "Patient", dataIndex: "patientName" },
    { title: "Doctor", dataIndex: "doctorName" },
    { title: "Amount", dataIndex: "totalAmount", align: "right", width: 120, render: fmt, sorter: (a, b) => (a.totalAmount || 0) - (b.totalAmount || 0) },
    { title: "Paid", dataIndex: "paidAmount", align: "right", width: 110, render: (v) => <span className="text-success">{fmt(v)}</span> },
    { title: "Balance", dataIndex: "balanceAmount", align: "right", width: 110, render: (v) => <span className={v > 0 ? "text-danger" : "text-success"}>{fmt(v)}</span> },
    {
      title: "Status", dataIndex: "status", width: 110,
      render: (v) => <span className={`badge bg-${STATUS_BADGE[v] || "secondary"}`}>{v === "PartialPaid" ? "Partial" : v}</span>,
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title"><h4>Sales Report</h4><h6>Invoice-wise sales summary</h6></div>
        </div>
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-auto">
                <label className="form-label small mb-1">From</label>
                <input type="date" className="form-control form-control-sm" value={filters.from}
                  onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} />
              </div>
              <div className="col-auto">
                <label className="form-label small mb-1">To</label>
                <input type="date" className="form-control form-control-sm" value={filters.to}
                  onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} />
              </div>
              <div className="col" style={{ minWidth: 220, maxWidth: 280 }}>
                <label className="form-label small mb-1">Patient</label>
                <Select
                  classNamePrefix="react-select"
                  options={patientOptions}
                  onInputChange={(v) => setPatientSearch(v)}
                  onChange={(opt) => setFilters((f) => ({ ...f, patientId: opt?.value || "" }))}
                  value={
                    filters.patientId
                      ? (() => {
                          const p = patients.find((x) => x.id === filters.patientId);
                          return p ? { value: p.id, label: `${p.patientName} — ${p.mobileNumber}` } : null;
                        })()
                      : null
                  }
                  placeholder="All Patients"
                  isClearable
                  noOptionsMessage={() => "No patients found"}
                />
              </div>
              <div className="col" style={{ minWidth: 160, maxWidth: 200 }}>
                <label className="form-label small mb-1">Status</label>
                <Select classNamePrefix="react-select" options={STATUS_OPTIONS}
                  value={STATUS_OPTIONS.find((o) => o.value && o.value === filters.status) || null}
                  placeholder="All Statuses"
                  isClearable
                  onChange={(opt) => setFilters((f) => ({ ...f, status: opt?.value || "" }))} />
              </div>
              <div className="col-auto">
                <button className="btn btn-primary" onClick={() => runReport()} disabled={loading}>
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
              <div className="col-auto">
                <button className="btn btn-dark" onClick={handleReset} disabled={loading}>
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        {summary && (
          <div className="row mb-3">
            {[
              { label: "Total Sales", value: fmt(summary.totalAmount), color: "primary" },
              { label: "Total Collected", value: fmt(summary.paidAmount), color: "success" },
              { label: "Outstanding", value: fmt(summary.balanceAmount), color: "danger" },
              { label: "Invoices", value: summary.count, color: "info" },
            ].map((s) => (
              <div key={s.label} className="col-6 col-md-3 mb-2">
                <div className={`card border-${s.color} text-center py-2`}>
                  <div className={`text-${s.color} fs-6 fw-bold`}>{s.value}</div>
                  <div className="small text-muted">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-responsive">
              <Table columns={columns} dataSource={data} loading={loading} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesReport;
